import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { MiniGameSession, LightningRoundState, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { GameQuestion } from '../../../interfaces/question.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

@Component({
  selector: 'app-lightning-round-game',
  templateUrl: './lightning-round-game.component.html',
  styleUrls: ['./lightning-round-game.component.scss']
})
export class LightningRoundGameComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  currentQuestion: GameQuestion | null = null;
  selectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  showFlash = false;
  flashCorrect = false;
  showingStudent = false;
  timerValue = 0;
  currentStudent: MiniGameStudent | null = null;
  private studentIndex = 0;
  private resultAudio: HTMLAudioElement | null = null;
  private pendingTimeouts: any[] = [];
  private destroyed = false;
  showInfo = false;

  get state(): LightningRoundState | null {
    return this.session?.gameState as LightningRoundState || null;
  }

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.session = session;
        if (session) {
          this.currentQuestion = session.questions[session.currentQuestionIndex] || null;
        }
      });

    this.miniGameService.timerValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => this.timerValue = val);

    this.miniGameService.timerExpired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.showFlash) {
          this.onTimerExpired();
        }
      });

    // Show first student spotlight then start
    this.assignNextStudent();
    this.showStudentSpotlight();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.gameAudioService.stop(this.resultAudio);
    this.gameAudioService.stopCountdownAudio();
    this.destroy$.next();
    this.destroy$.complete();
    this.miniGameService.stopTimer();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  private showStudentSpotlight(): void {
    this.showingStudent = true;
    this.safeTimeout(() => {
      this.showingStudent = false;
      this.startQuestionTimer();
    }, 3500);
  }

  private startQuestionTimer(): void {
    this.miniGameService.startTimer(10);
    this.gameAudioService.startCountdownAudio(this.miniGameService.timerValue$);
  }

  selectOption(optionId: 'A' | 'B' | 'C' | 'D'): void {
    this.selectedOption = optionId;
    // Auto-submit on select for lightning speed
    this.submitAnswer();
  }

  onTimerExpired(): void {
    this.gameAudioService.stopCountdownAudio();
    this.miniGameService.submitAnswer(null, {
      studentId: this.currentStudent?.id
    });
    this.playResultSound(false);
    this.flashCorrect = false;
    this.showFlash = true;

    this.safeTimeout(() => {
      this.showFlash = false;
      this.miniGameService.nextQuestion();
      this.selectedOption = null;
      if (this.session && this.session.currentQuestionIndex < this.session.questions.length) {
        this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
        this.assignNextStudent();
        this.showStudentSpotlight();
      }
    }, 800);
  }

  submitAnswer(): void {
    if (!this.selectedOption) return;
    this.gameAudioService.stopCountdownAudio();
    this.miniGameService.submitAnswer(this.selectedOption, {
      studentId: this.currentStudent?.id
    });

    // Brief flash
    const result = this.state;
    this.flashCorrect = result?.flashResult === 'correct';
    this.playResultSound(this.flashCorrect);
    this.showFlash = true;

    this.safeTimeout(() => {
      this.showFlash = false;
      this.miniGameService.nextQuestion();
      this.selectedOption = null;
      if (this.session && this.session.currentQuestionIndex < this.session.questions.length) {
        this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
        this.assignNextStudent();
        this.showStudentSpotlight();
      }
    }, 800);
  }

  private playResultSound(correct: boolean): void {
    this.gameAudioService.stop(this.resultAudio);
    const file = correct ? 'fastSoundEffect.mp3' : 'buzzerSoundEffect.mp3';
    this.resultAudio = this.gameAudioService.play(`assets/game-mode/sound-effects/${file}`);
  }

  private assignNextStudent(): void {
    if (!this.session) return;
    const students = this.session.students;
    if (!students.length) return;
    this.currentStudent = students[this.studentIndex % students.length];
    this.studentIndex++;
    this.miniGameService.setCurrentStudent(this.currentStudent.id);
  }

  getCurrentStudentName(): string {
    if (!this.currentStudent) return '';
    return this.currLang === 'ar' ? this.currentStudent.name.ar : this.currentStudent.name.en;
  }

  getQuestionNum(): number {
    return (this.session?.currentQuestionIndex || 0) + 1;
  }

  getTotalQuestions(): number {
    return this.session?.questions.length || 12;
  }

  quitGame(): void {
    this.miniGameService.finishMiniGame();
  }

  getIntensityClass(): string {
    const i = this.state?.intensity || 0;
    if (i >= 3) return 'intensity-max';
    if (i >= 2) return 'intensity-high';
    if (i >= 1) return 'intensity-mid';
    return '';
  }
}
